import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  TrendingUp, TrendingDown, Minus, AlertTriangle, 
  Target, Shield, Zap, Clock, DollarSign, 
  ArrowUp, ArrowDown, Activity, CheckCircle2, XCircle
} from "lucide-react";
import { toast } from "sonner";

import { API } from "../config/api";

// Signal strength levels
const SIGNAL_LEVELS = {
  STRONG_LONG: { label: "Strong Long", color: "bg-green-600", textColor: "text-white", icon: ArrowUp, description: "High conviction bullish - all factors aligned" },
  LONG_BIAS: { label: "Long Bias", color: "bg-green-100", textColor: "text-green-800", icon: TrendingUp, description: "Moderate bullish - most factors positive" },
  NEUTRAL: { label: "Neutral", color: "bg-slate-100", textColor: "text-slate-800", icon: Minus, description: "No clear edge - wait for setup" },
  SHORT_BIAS: { label: "Short Bias", color: "bg-red-100", textColor: "text-red-800", icon: TrendingDown, description: "Moderate bearish - most factors negative" },
  STRONG_SHORT: { label: "Strong Short", color: "bg-red-600", textColor: "text-white", icon: ArrowDown, description: "High conviction bearish - all factors aligned" }
};

const MasterSignal = ({ ticker, tradingProfile, basketHealthData }) => {
  const [masterSignal, setMasterSignal] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateMasterSignal = useCallback(async () => {
    if (!ticker) return;

    setLoading(true);
    try {
      // Fetch additional data needed for master signal calculation
      const [technicalRes, sentimentRes] = await Promise.all([
        axios.get(`${API}/stock/intraday-trading-signals/${ticker}`).catch(() => ({ data: {} })),
        axios.get(`${API}/stock/sentiment-fast/${ticker}`).catch(() => ({ data: {} }))
      ]);

      const technical = technicalRes.data || {};
      const sentiment = sentimentRes.data || {};
      const basket = basketHealthData || technical.basket_health || {};

      // Calculate component scores (-100 to +100)
      const scores = {
        trend: calculateTrendScore(technical),
        basket: calculateBasketScore(basket),
        momentum: calculateMomentumScore(technical),
        sentiment: calculateSentimentScore(sentiment),
        volume: calculateVolumeScore(technical)
      };

      // Weight scores based on selected strategies
      const weights = getStrategyWeights(tradingProfile?.strategies || ["intraday_etf_options"]);
      
      // Calculate weighted master score
      let totalWeight = 0;
      let weightedScore = 0;
      
      for (const [key, score] of Object.entries(scores)) {
        const weight = weights[key] || 1;
        weightedScore += score * weight;
        totalWeight += weight;
      }

      const masterScore = totalWeight > 0 ? weightedScore / totalWeight : 0;

      // Determine signal level
      let signalLevel;
      if (masterScore >= 60) signalLevel = "STRONG_LONG";
      else if (masterScore >= 25) signalLevel = "LONG_BIAS";
      else if (masterScore >= -25) signalLevel = "NEUTRAL";
      else if (masterScore >= -60) signalLevel = "SHORT_BIAS";
      else signalLevel = "STRONG_SHORT";

      // Check for conflicting signals (contrarian warning)
      const conflicts = detectConflicts(scores);

      // Generate trade recommendation
      const recommendation = generateRecommendation(signalLevel, scores, tradingProfile, technical, ticker);

      // Calculate position sizing
      const positionSizing = calculatePositionSize(tradingProfile, technical, signalLevel);

      setMasterSignal({
        level: signalLevel,
        score: masterScore,
        scores,
        weights,
        conflicts,
        recommendation,
        positionSizing,
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error("Error calculating master signal:", error);
      toast.error("Failed to calculate master signal");
    } finally {
      setLoading(false);
    }
  }, [ticker, basketHealthData, tradingProfile]);

  useEffect(() => {
    if (ticker) {
      // Calculate signal even if basket data isn't available yet
      calculateMasterSignal();
    }
  }, [ticker, basketHealthData, calculateMasterSignal, tradingProfile]);

  // Score calculation functions
  const calculateTrendScore = (technical) => {
    let score = 0;
    const trend = technical.trend?.toLowerCase() || '';
    const priceVsVwap = technical.price_vs_vwap || 0;
    
    if (trend.includes('uptrend')) score += 40;
    else if (trend.includes('downtrend')) score -= 40;
    
    // Price vs VWAP
    if (priceVsVwap > 1) score += 20;
    else if (priceVsVwap < -1) score -= 20;
    
    return Math.max(-100, Math.min(100, score));
  };

  const calculateBasketScore = (basket) => {
    const breadth = basket.breadth_score || 50;
    const health = basket.basket_health?.toLowerCase() || '';
    
    let score = (breadth - 50) * 2; // -100 to +100
    
    if (health.includes('strong')) score += 20;
    else if (health.includes('weak')) score -= 20;
    
    return Math.max(-100, Math.min(100, score));
  };

  const calculateMomentumScore = (technical) => {
    const rsi = technical.rsi || 50;
    const mfi = technical.mfi || 50;
    
    let score = 0;
    
    // RSI scoring
    if (rsi > 70) score -= 30; // Overbought
    else if (rsi < 30) score += 30; // Oversold
    else score += (rsi - 50) * 0.5;
    
    // MFI scoring
    if (mfi > 80) score -= 20;
    else if (mfi < 20) score += 20;
    else score += (mfi - 50) * 0.3;
    
    return Math.max(-100, Math.min(100, score));
  };

  const calculateSentimentScore = (sentiment) => {
    const analysis = sentiment.analysis?.toLowerCase() || '';
    let score = 0;
    
    if (analysis.includes('strong') && analysis.includes('bullish')) score = 60;
    else if (analysis.includes('bullish')) score = 30;
    else if (analysis.includes('strong') && analysis.includes('bearish')) score = -60;
    else if (analysis.includes('bearish')) score = -30;
    
    return score;
  };

  const calculateVolumeScore = (technical) => {
    const volumeRatio = technical.volume_ratio || 1;
    let score = 0;
    
    if (volumeRatio > 1.5) score = 30;
    else if (volumeRatio > 1.2) score = 15;
    else if (volumeRatio < 0.5) score = -20;
    
    return score;
  };

  const getStrategyWeights = (strategies) => {
    const weights = { trend: 1, basket: 1, momentum: 1, sentiment: 1, volume: 1 };
    
    if (strategies.includes("intraday_etf_options")) {
      weights.trend = 3;
      weights.basket = 2.5;
      weights.momentum = 2;
    }
    if (strategies.includes("scalping")) {
      weights.momentum = 3;
      weights.volume = 2;
      weights.trend = 2;
    }
    if (strategies.includes("swing_mega_caps")) {
      weights.sentiment = 2;
      weights.trend = 2;
      weights.basket = 1.5;
    }
    if (strategies.includes("regime_timing")) {
      weights.basket = 3;
      weights.trend = 2;
      weights.sentiment = 2;
    }
    
    return weights;
  };

  const detectConflicts = (scores) => {
    const conflicts = [];
    
    // Trend vs Basket conflict
    if ((scores.trend > 30 && scores.basket < -30) || (scores.trend < -30 && scores.basket > 30)) {
      conflicts.push("⚠️ Trend and basket health are conflicting");
    }
    
    // Momentum divergence
    if ((scores.momentum > 40 && scores.trend < -20) || (scores.momentum < -40 && scores.trend > 20)) {
      conflicts.push("⚠️ Momentum divergence detected");
    }
    
    return conflicts;
  };

  const generateRecommendation = (signalLevel, scores, profile, technical, ticker) => {
    const strategies = profile?.strategies || ["intraday_etf_options"];
    const dtePreference = profile?.dtePreference || "1-3dte";
    
    let action = "";
    let instrument = "";
    let timing = "";
    let invalidation = "";
    
    const isOptionsStrategy = strategies.includes("intraday_etf_options") || strategies.includes("scalping");
    
    switch (signalLevel) {
      case "STRONG_LONG":
        action = isOptionsStrategy ? "BUY CALLS" : "GO LONG";
        instrument = isOptionsStrategy ? `${ticker} ${getDTELabel(dtePreference)} ATM Calls` : `${ticker} Stock`;
        timing = "Enter on next pullback to VWAP or immediate if momentum strong";
        invalidation = `Below ${(technical.support || 0).toFixed?.(2) || "0.00"} or basket breadth < 40%`;
        break;
      case "LONG_BIAS":
        action = isOptionsStrategy ? "CONSIDER CALLS" : "LEAN LONG";
        instrument = isOptionsStrategy ? `${ticker} ${getDTELabel(dtePreference)} ATM/OTM Calls` : `${ticker} Stock`;
        timing = "Wait for pullback entry or confirmation of support hold";
        invalidation = `Break below ${(technical.support || 0).toFixed?.(2) || "0.00"}`;
        break;
      case "NEUTRAL":
        action = "HOLD";
        instrument = "N/A";
        timing = "Wait for clearer setup - choppy conditions";
        invalidation = "N/A";
        break;
      case "SHORT_BIAS":
        action = isOptionsStrategy ? "CONSIDER PUTS" : "LEAN SHORT";
        instrument = isOptionsStrategy ? `${ticker} ${getDTELabel(dtePreference)} ATM/OTM Puts` : `${ticker} Short`;
        timing = "Wait for bounce to resistance for entry";
        invalidation = `Break above ${(technical.resistance || 0).toFixed?.(2) || "0.00"}`;
        break;
      case "STRONG_SHORT":
        action = isOptionsStrategy ? "BUY PUTS" : "GO SHORT";
        instrument = isOptionsStrategy ? `${ticker} ${getDTELabel(dtePreference)} ATM Puts` : `${ticker} Short`;
        timing = "Enter on bounce to VWAP or immediate if momentum strong";
        invalidation = `Above ${(technical.resistance || 0).toFixed?.(2) || "0.00"} or basket breadth > 60%`;
        break;
      default:
        action = "WAIT";
    }
    
    return { action, instrument, timing, invalidation };
  };

  const getDTELabel = (dte) => {
    switch (dte) {
      case "0dte": return "0DTE";
      case "1-3dte": return "2DTE";
      case "weekly": return "Weekly";
      case "monthly": return "Monthly";
      default: return "Weekly";
    }
  };

  const calculatePositionSize = (profile, technical, signalLevel) => {
    const accountSize = parseInt(profile?.accountSize || 25000);
    const riskPercent = parseFloat(profile?.riskPerTrade || 1) / 100;
    const maxRisk = accountSize * riskPercent;
    
    const currentPrice = technical.current_price || 100;
    const stopDistance = Math.abs(currentPrice - (technical.support || currentPrice * 0.98));
    const stopPercent = (stopDistance / currentPrice) * 100;
    
    // Calculate shares/contracts
    const sharesAtRisk = Math.floor(maxRisk / stopDistance);
    const positionValue = sharesAtRisk * currentPrice;
    
    // For options, estimate contract quantity (rough)
    const optionPrice = currentPrice * 0.02; // Rough estimate
    const contracts = Math.floor(maxRisk / (optionPrice * 100));
    
    // Confidence adjustment
    const confidenceMultiplier = (signalLevel === "STRONG_LONG" || signalLevel === "STRONG_SHORT") ? 1 : 0.7;
    
    return {
      maxRisk: (maxRisk || 0).toFixed(0),
      shares: Math.floor((sharesAtRisk || 0) * confidenceMultiplier),
      contracts: Math.max(1, Math.floor((contracts || 0) * confidenceMultiplier)),
      positionValue: ((positionValue || 0) * confidenceMultiplier).toFixed(0),
      stopPercent: (stopPercent || 0).toFixed(1)
    };
  };

  if (loading || !masterSignal) {
    return (
      <Card className="border-2 border-slate-300 bg-gradient-to-r from-slate-50 to-blue-50">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Activity className="w-6 h-6 animate-pulse text-blue-600" />
            <span className="ml-2 text-sm text-slate-600">Calculating Master Signal...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const signal = SIGNAL_LEVELS[masterSignal.level];
  const SignalIcon = signal.icon;

  return (
    <Card className="border-2 border-slate-300 bg-white shadow-lg overflow-hidden">
      {/* At-a-Glance Header Strip */}
      <div className={`${signal.color} px-4 py-3`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${masterSignal.level.includes('STRONG') ? 'bg-white/20' : 'bg-white/40'}`}>
              <SignalIcon className={`w-6 h-6 ${signal.textColor}`} />
            </div>
            <div>
              <h2 className={`text-xl font-bold ${signal.textColor}`}>
                {signal.label}
              </h2>
              <p className={`text-xs ${signal.textColor} opacity-90`}>
                {signal.description}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-2xl font-bold ${signal.textColor}`}>
              {masterSignal.score > 0 ? '+' : ''}{masterSignal.score.toFixed?.(0) || "0"}
            </div>
            <div className={`text-xs ${signal.textColor} opacity-80`}>
              Conviction Score
            </div>
          </div>
        </div>
      </div>

      <CardContent className="p-4 space-y-4">
        {/* Immediate Action */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-lg border-2 border-indigo-200">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-indigo-600" />
            <span className="font-bold text-indigo-900">Immediate Action</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-indigo-600 font-medium">Action</p>
              <p className="text-lg font-bold text-indigo-900">{masterSignal.recommendation.action}</p>
            </div>
            <div>
              <p className="text-xs text-indigo-600 font-medium">Instrument</p>
              <p className="text-sm font-medium text-indigo-800">{masterSignal.recommendation.instrument}</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-indigo-200">
            <p className="text-xs text-indigo-700">
              <span className="font-semibold">Timing:</span> {masterSignal.recommendation.timing}
            </p>
            <p className="text-xs text-indigo-700 mt-1">
              <span className="font-semibold">Invalidation:</span> {masterSignal.recommendation.invalidation}
            </p>
          </div>
        </div>

        {/* Position Sizing */}
        <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-slate-600" />
            <span className="font-semibold text-slate-800 text-sm">Position Sizing</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div className="text-center p-2 bg-white rounded border">
              <p className="text-xs text-slate-500">Max Risk</p>
              <p className="text-sm font-bold text-slate-800">${masterSignal.positionSizing.maxRisk}</p>
            </div>
            <div className="text-center p-2 bg-white rounded border">
              <p className="text-xs text-slate-500">Shares</p>
              <p className="text-sm font-bold text-slate-800">{masterSignal.positionSizing.shares}</p>
            </div>
            <div className="text-center p-2 bg-white rounded border">
              <p className="text-xs text-slate-500">Contracts</p>
              <p className="text-sm font-bold text-slate-800">{masterSignal.positionSizing.contracts}</p>
            </div>
            <div className="text-center p-2 bg-white rounded border">
              <p className="text-xs text-slate-500">Stop %</p>
              <p className="text-sm font-bold text-slate-800">{masterSignal.positionSizing.stopPercent}%</p>
            </div>
          </div>
        </div>

        {/* Component Scores */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-700">Signal Components</p>
          <div className="grid grid-cols-5 gap-2">
            {Object.entries(masterSignal.scores).map(([key, score]) => (
              <div key={key} className="text-center p-2 bg-slate-50 rounded border">
                <p className="text-[10px] text-slate-500 capitalize">{key}</p>
                <p className={`text-sm font-bold ${score > 20 ? 'text-green-600' : score < -20 ? 'text-red-600' : 'text-slate-600'}`}>
                  {score > 0 ? '+' : ''}{score.toFixed?.(0) || "0"}
                </p>
                <div className={`h-1 mt-1 rounded-full ${score > 20 ? 'bg-green-400' : score < -20 ? 'bg-red-400' : 'bg-slate-300'}`} 
                     style={{ width: `${Math.min(100, Math.abs(score))}%`, marginLeft: score < 0 ? 'auto' : '0' }} />
              </div>
            ))}
          </div>
        </div>

        {/* Conflicts/Warnings */}
        {masterSignal.conflicts.length > 0 && (
          <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <span className="font-semibold text-yellow-800 text-sm">Signal Conflicts</span>
            </div>
            {masterSignal.conflicts.map((conflict, idx) => (
              <p key={idx} className="text-xs text-yellow-700">{conflict}</p>
            ))}
          </div>
        )}

        {/* Alignment Checklist */}
        <div className="grid grid-cols-2 gap-2">
          <div className={`p-2 rounded border flex items-center gap-2 ${masterSignal.scores.trend > 10 ? 'bg-green-50 border-green-200' : masterSignal.scores.trend < -10 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
            {masterSignal.scores.trend > 10 ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : masterSignal.scores.trend < -10 ? <XCircle className="w-4 h-4 text-red-600" /> : <Minus className="w-4 h-4 text-slate-400" />}
            <span className="text-xs">Intraday Trend</span>
          </div>
          <div className={`p-2 rounded border flex items-center gap-2 ${masterSignal.scores.basket > 10 ? 'bg-green-50 border-green-200' : masterSignal.scores.basket < -10 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
            {masterSignal.scores.basket > 10 ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : masterSignal.scores.basket < -10 ? <XCircle className="w-4 h-4 text-red-600" /> : <Minus className="w-4 h-4 text-slate-400" />}
            <span className="text-xs">Basket Health</span>
          </div>
          <div className={`p-2 rounded border flex items-center gap-2 ${masterSignal.scores.momentum > 10 ? 'bg-green-50 border-green-200' : masterSignal.scores.momentum < -10 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
            {masterSignal.scores.momentum > 10 ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : masterSignal.scores.momentum < -10 ? <XCircle className="w-4 h-4 text-red-600" /> : <Minus className="w-4 h-4 text-slate-400" />}
            <span className="text-xs">Momentum</span>
          </div>
          <div className={`p-2 rounded border flex items-center gap-2 ${masterSignal.scores.sentiment > 10 ? 'bg-green-50 border-green-200' : masterSignal.scores.sentiment < -10 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
            {masterSignal.scores.sentiment > 10 ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : masterSignal.scores.sentiment < -10 ? <XCircle className="w-4 h-4 text-red-600" /> : <Minus className="w-4 h-4 text-slate-400" />}
            <span className="text-xs">Sentiment</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MasterSignal;
